/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import type {
  AdvertiserMetadata,
  ExtractionConfig,
  ExtractionResult,
  ExtractionRule,
  TransformType,
} from '../../types/advertorial';

/**
 * Extracts advertiser metadata from DOM elements based on configured rules
 */
export class MetadataExtractor {
  private readonly config: ExtractionConfig;

  constructor(config: ExtractionConfig) {
    this.config = config;
  }

  /**
   * Extract metadata from an element
   * @param element The advertorial DOM element
   * @returns Extraction result with metadata and any errors
   */
  public extract(element: HTMLElement): ExtractionResult {
    const metadata: AdvertiserMetadata = {};
    const errors: Array<{ field: string; error: string }> = [];

    for (const [fieldName, rule] of Object.entries(this.config)) {
      try {
        const value = this.extractField(element, rule);
        if (value !== null && value !== undefined) {
          metadata[fieldName] = value;
        } else if (rule.default) {
          metadata[fieldName] = rule.default;
        }
      } catch (e) {
        const error = e instanceof Error ? e.message : String(e);
        errors.push({ field: fieldName, error });

        // Use default value if available
        if (rule.default) {
          metadata[fieldName] = rule.default;
        }
      }
    }

    return { metadata, errors };
  }

  /**
   * Extract a single field value
   */
  private extractField(element: HTMLElement, rule: ExtractionRule): string | undefined {
    let value: string | undefined;

    switch (rule.type) {
      case 'url-parameter':
        value = this.extractFromUrlParameter(element, rule);
        break;
      case 'attribute':
        value = this.extractFromAttribute(element, rule);
        break;
      case 'element-text':
        value = this.extractFromElementText(element, rule);
        break;
      case 'regex':
        value = this.extractFromRegex(element, rule);
        break;
      default:
        throw new Error(`Unknown extraction type: ${(rule as any).type}`);
    }

    // Apply transforms if value was extracted
    if (value !== undefined && value !== null && rule.transform) {
      value = this.applyTransforms(value, rule.transform);
    }

    return value;
  }

  /**
   * Extract value from URL parameter
   */
  private extractFromUrlParameter(element: HTMLElement, rule: ExtractionRule): string | undefined {
    if (!rule.parameter) {
      throw new Error('parameter is required for url-parameter extraction');
    }

    // Find link element
    const linkElement = rule.selector
      ? element.querySelector<HTMLAnchorElement>(rule.selector)
      : element.querySelector<HTMLAnchorElement>('a[href]');

    if (!linkElement || !linkElement.href) {
      return undefined;
    }

    try {
      const url = new URL(linkElement.href);
      return url.searchParams.get(rule.parameter) || undefined;
    } catch (e) {
      return undefined;
    }
  }

  /**
   * Extract value from element attribute
   */
  private extractFromAttribute(element: HTMLElement, rule: ExtractionRule): string | undefined {
    if (!rule.attribute) {
      throw new Error('attribute is required for attribute extraction');
    }

    if (!rule.selector) {
      throw new Error('selector is required for attribute extraction');
    }

    // Find target element
    const targetElement = element.querySelector<HTMLElement>(rule.selector);
    if (!targetElement) {
      return undefined;
    }

    const value = targetElement.getAttribute(rule.attribute);
    return value || undefined;
  }

  /**
   * Extract value from element text content
   */
  private extractFromElementText(element: HTMLElement, rule: ExtractionRule): string | undefined {
    if (!rule.selector) {
      throw new Error('selector is required for element-text extraction');
    }

    // Find target element
    const targetElement = element.querySelector<HTMLElement>(rule.selector);
    if (!targetElement) {
      return undefined;
    }

    const text = targetElement.textContent;
    if (!text) {
      return undefined;
    }

    const trimmed = text.trim();
    return trimmed !== '' ? trimmed : undefined;
  }

  /**
   * Extract value using regex pattern
   */
  private extractFromRegex(element: HTMLElement, rule: ExtractionRule): string | undefined {
    if (!rule.pattern) {
      throw new Error('pattern is required for regex extraction');
    }

    // Get content to match against
    let content: string;
    if (rule.selector) {
      const targetElement = element.querySelector<HTMLElement>(rule.selector);
      if (!targetElement) {
        return undefined;
      }
      // Use innerHTML for regex matching to support matching HTML structure
      content = targetElement.innerHTML;
    } else {
      content = element.innerHTML;
    }

    // Apply regex
    try {
      const regex = new RegExp(rule.pattern, rule.flags || '');
      const match = content.match(regex);

      if (match) {
        // Return first capture group if available, otherwise full match
        return match[1] !== undefined ? match[1] : match[0];
      }
    } catch (e) {
      throw new Error(`Invalid regex pattern: ${rule.pattern}`);
    }

    return undefined;
  }

  /**
   * Apply transform functions to a value
   */
  private applyTransforms(value: string, transforms: TransformType | TransformType[]): string {
    const transformList = Array.isArray(transforms) ? transforms : [transforms];

    let result = value;
    for (const transform of transformList) {
      result = this.applyTransform(result, transform);
    }

    return result;
  }

  /**
   * Apply a single transform function
   */
  private applyTransform(value: string, transform: TransformType): string {
    switch (transform) {
      case 'strip-utm':
        return this.stripUtmParameters(value);
      case 'strip-query':
        return this.stripQueryParameters(value);
      case 'decode-uri':
        return this.decodeUri(value);
      case 'lowercase':
        return value.toLowerCase();
      case 'trim':
        return value.trim();
      default:
        return value;
    }
  }

  /**
   * Strip UTM parameters from URL
   */
  private stripUtmParameters(value: string): string {
    try {
      const url = new URL(value);
      const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

      for (const param of utmParams) {
        url.searchParams.delete(param);
      }

      return url.toString();
    } catch (e) {
      // Not a valid URL, return as-is
      return value;
    }
  }

  /**
   * Strip all query parameters from URL
   */
  private stripQueryParameters(value: string): string {
    try {
      const url = new URL(value);
      url.search = '';
      return url.toString();
    } catch (e) {
      // Not a valid URL, return as-is
      return value;
    }
  }

  /**
   * Decode URI component
   */
  private decodeUri(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch (e) {
      // Invalid URI encoding, return as-is
      return value;
    }
  }
}
